import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import type {
  TriaxialCrossTest,
  TriaxialGeneral,
  TriaxialReading,
  TriaxialTestType,
  TriaxialUploadResult,
} from '../../../types/triaxial';
import { uploadTriaxialGds } from '../../../services/triaxialService';
import { computeFinalDryMassG } from './computations';
import { consolidationColumns, buildShearColumns, type TriaxialResultColumn } from './triaxialResultColumns';
import { FormulaHeaderTooltip } from '../components/FormulaHeaderTooltip';
import { useTestFormulas } from '../../../hooks/useTestFormulas';

interface Props {
  workOrderDetailKey: string;
  general: TriaxialGeneral;
  readings: TriaxialReading[];
  /**
   * Server-side count of 'Excluded' rows. These are no longer shipped inside
   * `readings` (performance), so the "N rows excluded" hint reads this instead.
   */
  excludedReadingsCount?: number;
  crossTest: TriaxialCrossTest | null;
  onReloadRequested: () => void;
  /**
   * True when the parent has unsaved Parameters/Stages edits. The upload
   * button is disabled in this case — the backend will 400 ("Save Parameters
   * first") if we let the user submit before a TriaxialGeneral row exists.
   */
  hasUnsavedParameters?: boolean;
  readOnly?: boolean;
}

/**
 * Inputs that backend `recomputeAndPersist` requires for the shear-row block
 * to run (mirrors the gates at backend/src/services/triaxialService.ts:605 +
 * :626 — `initial.initialVolumeMm3 && initial.massDryG && effectiveGs &&
 * consRaw.length >= 2`, plus `postCons.reportedAreaM2`). Returned labels match
 * the field labels on the Parameters tab so the user can find them.
 */
function listMissingShearCalcInputs(
  g: TriaxialGeneral,
  crossTest: TriaxialCrossTest | null,
  consolidationRowCount: number,
): string[] {
  const missing: string[] = [];

  const effectiveGs = g.specificGravityGs ?? crossTest?.specificGravityGs ?? null;
  if (effectiveGs === null) missing.push('Specific Gravity Gs');

  if (g.massOfPanG === null) missing.push('Mass of Former');
  if (g.massWetSamplePlusPanG === null) missing.push('Initial Wet Mass + Former');

  // BUG-2026-0062: BUG-2026-0053 removed the "Initial Dry Mass" input from
  // Parameters — the dry mass is now derived from the post-test tin-based
  // pan weighing in Final Properties. Mirror `computeInitialProperties.
  // massDryG` so this gate doesn't flag a now-unreachable field.
  const dryFromTin = computeFinalDryMassG(g.massOfTinG, g.finalDryMassPlusTin1G);
  const dryLegacy =
    g.massDrySamplePlusPanG !== null && g.massOfPanG !== null
      ? g.massDrySamplePlusPanG - g.massOfPanG - (g.massExtraG ?? 0)
      : null;
  const haveDryMass =
    (dryFromTin !== null && dryFromTin > 0) || (dryLegacy !== null && dryLegacy > 0);
  if (!haveDryMass) {
    missing.push('Final Dry Mass (Final Properties → Mass of Pan + Final Dry Mass + Pan)');
  }

  const haveGeometry =
    (g.diameter1 !== null || (g.perimeterMm !== null && g.perimeterMm > 0))
    && g.length1 !== null;
  const haveInitialVolumeOverride = g.initialVolumeMm3 !== null && g.initialVolumeMm3 > 0;
  if (!haveGeometry && !haveInitialVolumeOverride) {
    missing.push('Specimen geometry (Diameter 1 + Length 1, or Initial Volume override)');
  }

  if (consolidationRowCount < 2) {
    missing.push('At least 2 Consolidation rows (upload a GDS file covering the configured Consolidation stages)');
  }

  return missing;
}

const fmt = (v: number | null | undefined, d = 2): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
};

export const TriaxialResultEntry = ({
  workOrderDetailKey, general, readings, excludedReadingsCount, crossTest, onReloadRequested, hasUnsavedParameters, readOnly,
}: Props) => {
  const theme = useTheme();
  const { formulaFor } = useTestFormulas('triaxial');
  const fileRef = useRef<HTMLInputElement>(null);
  const [subTab, setSubTab] = useState<0 | 1>(0);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<TriaxialUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const consRows = useMemo(() => readings.filter((r) => r.phase === 'Consolidation'), [readings]);
  const shearRows = useMemo(() => readings.filter((r) => r.phase === 'Shear'), [readings]);
  // Excluded rows are still in the DB but don't match the current Consolidation
  // range or Shear stage — surface the count so the user knows there's data
  // available if they widen their stage selection. See BUG-2026-0039 follow-up.
  // The count now comes from the server (excludedReadingsCount) because the
  // Excluded rows themselves are no longer shipped in `readings` (perf); fall
  // back to counting any Excluded rows still present for backward-compat.
  const excludedRowsCount = useMemo(
    () => excludedReadingsCount ?? readings.filter((r) => r.phase === 'Excluded').length,
    [excludedReadingsCount, readings],
  );
  const testType: TriaxialTestType = (general.testType as TriaxialTestType) ?? 'CIU';

  // Shear-calc gate diagnostic: when shear rows exist but every computed column
  // is null, the backend's `recomputeAndPersist` short-circuited the shear block
  // because postCons couldn't be derived. List the specific Parameters fields
  // the operator still needs to fill so they're not staring at a blank grid.
  const shearCalcsMissing =
    shearRows.length > 0 && shearRows.every((r) => r.computedAxialStrainPct === null);
  const missingShearInputs = useMemo(
    () => (shearCalcsMissing ? listMissingShearCalcInputs(general, crossTest, consRows.length) : []),
    [shearCalcsMissing, general, crossTest, consRows.length],
  );

  const onUploadClick = () => fileRef.current?.click();
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    setUploadResult(null);
    try {
      const result = await uploadTriaxialGds(workOrderDetailKey, file);
      setUploadResult(result);
      onReloadRequested();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const stageFieldsConfigured =
    general.consolidationFromStage !== null
    && general.consolidationToStage !== null
    && general.shearStageNumber !== null;
  const canUpload = !readOnly && stageFieldsConfigured && !hasUnsavedParameters;
  const uploadHint = !stageFieldsConfigured
    ? 'Configure Consolidation stages and Shear stage on the Parameters tab before uploading.'
    : hasUnsavedParameters
      ? 'You have unsaved Parameters. Save them before uploading a GDS file.'
      : null;

  // Header cell with a formula-on-hover tooltip (FEAT-2026-0009). Calculated
  // columns get a subtle dotted underline + help cursor to hint the formula.
  const renderHeaderCell = (col: TriaxialResultColumn, padding: string) => {
    const reg = formulaFor(col.columnKey);
    return (
    <FormulaHeaderTooltip key={col.label} label={col.label} formula={reg?.formula} note={reg?.note}>
      <th
        style={{
          padding,
          textAlign: 'right',
          fontWeight: 700,
          borderBottom: '1px solid #ccc',
          cursor: 'help',
          textDecoration: col.kind === 'calc' ? 'underline dotted' : undefined,
          textDecorationColor: col.kind === 'calc' ? alpha(theme.palette.text.primary, 0.35) : undefined,
          textUnderlineOffset: 3,
        }}
      >
        {col.label}
      </th>
    </FormulaHeaderTooltip>
    );
  };

  const shearColumns = buildShearColumns(testType);

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Upload bar ────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained" startIcon={<CloudUpload />} onClick={onUploadClick}
            disabled={!canUpload || uploading}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload GDS File'}
          </Button>
          <input
            type="file" ref={fileRef} style={{ display: 'none' }}
            accept=".gds,.txt,.csv" onChange={onFileChange}
          />
          {uploadHint && (
            <Typography variant="caption" color="text.secondary">
              {uploadHint}
            </Typography>
          )}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        {uploadResult && (() => {
          const eitherPhaseEmpty =
            uploadResult.rowsIngested > 0
            && (uploadResult.consolidationRowsIngested === 0 || uploadResult.shearRowsIngested === 0);
          const severity =
            uploadResult.rowsIngested === 0 || eitherPhaseEmpty ? 'warning' : 'success';
          const cs = uploadResult.configuredStages;
          const consRange = cs.consolidationFromStage !== null && cs.consolidationToStage !== null
            ? `${cs.consolidationFromStage}…${cs.consolidationToStage}`
            : '—';
          const shearStage = cs.shearStageNumber !== null ? String(cs.shearStageNumber) : '—';
          return (
            <Alert severity={severity} sx={{ mt: 1 }}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                Ingested {uploadResult.rowsIngested} rows
                {' — '}
                Consolidation: <strong>{uploadResult.consolidationRowsIngested}</strong>
                {', '}
                Shear: <strong>{uploadResult.shearRowsIngested}</strong>
                {uploadResult.excludedRowsIngested > 0 && (
                  <>
                    {', '}
                    Other stages held in reserve: <strong>{uploadResult.excludedRowsIngested}</strong>
                  </>
                )}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                File contains stages [{uploadResult.stagesDetected.join(', ') || 'none'}].
                {' '}Configured Consolidation = {consRange}, Shear = {shearStage}.
              </Typography>
              {uploadResult.warnings.length > 0 && (
                <Box component="ul" sx={{ pl: 2.5, mt: 0.75, mb: 0 }}>
                  {uploadResult.warnings.map((w, i) => (
                    <Typography key={i} variant="caption" component="li">{w}</Typography>
                  ))}
                </Box>
              )}
              {eitherPhaseEmpty && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  Adjust the Consolidation/Shear stage selection on the Parameters tab and Save — the rows held in reserve will re-bucket automatically without re-uploading.
                </Typography>
              )}
            </Alert>
          );
        })()}
      </Paper>

      {/* ── Calculations-skipped warning ──────────────────────────── */}
      {shearCalcsMissing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
            Shear-phase calculations were skipped — the Shear grid's computed columns (axial strain,
            deviator stress, p, p′, t, s′, Δu, …) will be blank until the inputs below are filled in.
          </Typography>
          {missingShearInputs.length > 0 ? (
            <>
              <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
                Fill the following on the <strong>Test Parameters</strong> tab, then click Save:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                {missingShearInputs.map((label) => (
                  <Typography key={label} variant="caption" component="li">{label}</Typography>
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              All required inputs appear to be filled — re-save the Test Parameters tab to re-run the calculations.
            </Typography>
          )}
          <Typography variant="caption" component="div" sx={{ mt: 0.75, fontStyle: 'italic' }}>
            The raw values from the GDS file are still shown in the grids below (including the
            <em> Axial Displ raw (mm) </em> column — column J of your file).
          </Typography>
        </Alert>
      )}

      {/* ── Sub-tabs ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v as 0 | 1)}>
          <Tab label={`Consolidation (${consRows.length} rows)`} />
          <Tab label={`Shear (${shearRows.length} rows)`} />
        </Tabs>
        {excludedRowsCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {excludedRowsCount} reading{excludedRowsCount === 1 ? '' : 's'} held in reserve (outside current stage selection). Adjust stages on Parameters and Save to bucket them.
          </Typography>
        )}
      </Box>

      {/* ── Consolidation grid ────────────────────────────────────── */}
      {subTab === 0 && (
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ overflow: 'auto', maxHeight: 500 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead style={{ background: alpha(theme.palette.primary.main, 0.08), position: 'sticky', top: 0 }}>
                <tr>
                  {consolidationColumns.map((col) => renderHeaderCell(col, '4px 8px'))}
                </tr>
              </thead>
              <tbody>
                {consRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                    No consolidation rows. Upload a GDS file with stages in the configured range.
                  </td></tr>
                )}
                {consRows.map((r) => (
                  <tr key={r.triaxialReadingKey} style={{ borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.05)}` }}>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{r.stageNumber}</td>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt(r.timeSinceStartOfTestS, 0)}</td>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt(r.axialDisplacementMm, 3)}</td>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt(r.backVolumeMm3, 0)}</td>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt(r.computedConsolidationTimeS, 0)}</td>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt(r.computedCorrectedBackVolumeMm3, 0)}</td>
                    <td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt(r.computedConsAxialDisplacementMm, 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Paper>
      )}

      {/* ── Shear grid ────────────────────────────────────────────── */}
      {subTab === 1 && (
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ overflow: 'auto', maxHeight: 500 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
              <thead style={{ background: alpha(theme.palette.primary.main, 0.08), position: 'sticky', top: 0 }}>
                <tr>
                  {shearColumns.map((col) => renderHeaderCell(col, '4px 6px'))}
                </tr>
              </thead>
              <tbody>
                {shearRows.length === 0 && (
                  <tr><td colSpan={20} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                    No shear rows.
                  </td></tr>
                )}
                {shearRows.map((r) => (
                  <tr key={r.triaxialReadingKey} style={{ borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.05)}` }}>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{r.stageNumber}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.timeSinceStartOfStageS, 0)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.porePressureKPa, 1)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.axialDisplacementMm, 3)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedAxialStrainPct, 3)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedShearAxialDisplacementMm, 3)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.loadCellKN, 4)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedEffSigma3KPa, 1)}</td>
                    {testType === 'CID' && (
                      <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedVolumeChangeMm3, 0)}</td>
                    )}
                    <td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: 600 }}>{fmt(r.computedDeviatorStressKPa, 1)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedCambridgePKPa, 1)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedEffCambridgePKPa, 1)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedMaxShearStressTKPa, 1)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedMeanStressSKPa, 1)}</td>
                    <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedEffStressRatio, 2)}</td>
                    {testType === 'CIU' && (
                      <td style={{ padding: '2px 6px', textAlign: 'right' }}>{fmt(r.computedExcessPwpKPa, 1)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
